/** Leave as marked days — the pure half.
 *
 *  No imports on purpose, so the Worker's suite can test it. Dates are ISO
 *  strings, the weekday rule is Monday-first as in `calendar.ts`, and a
 *  holiday set is any record keyed by ISO day (`countryHolidays(key, y)`; merge two
 *  years' records for a range that crosses New Year).
 *
 *  Its one import is `yearSheet.ts`, which has none, so the suite can still
 *  load it bare.
 */

import { leaveDiff } from './yearSheet.ts'

interface StayLike { kind: string; starts_on: string; ends_on: string; provisional: boolean }

const D = (s: string) => new Date(s + 'T12:00:00Z')
const iso = (d: Date) => d.toISOString().slice(0, 10)
const addDays = (s: string, n: number) => iso(new Date(D(s).getTime() + n * 86400000))
/** 0 Monday … 6 Sunday. */
const dow = (s: string) => (D(s).getUTCDay() + 6) % 7
const isWeekend = (s: string) => dow(s) >= 5
// `hasOwn`, not `in` or a bare index: a holiday record is a plain object.
const isHoliday = (s: string, h: Record<string, unknown>) => Object.hasOwn(h, s)

/** Spent, held and free for one year. `held` is a marked day that falls in a
 *  provisional stay — planned, not yet booked — and is not counted in
 *  `spent`. `free` is what is left after both. */
export function leaveStats(days: string[], year: number, allowance: number, stays: StayLike[]) {
  const prefix = `${year}-`
  const provisional = stays.filter(s => s.provisional)
  let spent = 0
  let held = 0
  for (const day of new Set(days)) {
    if (!day.startsWith(prefix)) continue
    if (provisional.some(s => day >= s.starts_on && day <= s.ends_on)) held++
    else spent++
  }
  return { spent, held, free: allowance - spent - held }
}

/** The weekdays of a travel stay that are not holidays of the region being looked at; `[]` for any
 *  other kind (a second home is work-from-there, not leave). */
export function defaultLeaveFor(stay: Pick<StayLike, 'kind' | 'starts_on' | 'ends_on'>, holidays: Record<string, unknown>): string[] {
  if (stay.kind !== 'travel') return []
  const out: string[] = []
  for (let d = stay.starts_on; d <= stay.ends_on; d = addDays(d, 1)) {
    if (!isWeekend(d) && !isHoliday(d, holidays)) out.push(d)
  }
  return out
}

/** A weekday sitting between a holiday and the weekend — the Friday after an
 *  Thursday holiday, the Monday before a Tuesday one. */
export function isBridgeDay(day: string, holidays: Record<string, unknown>): boolean {
  if (isWeekend(day) || isHoliday(day, holidays)) return false
  const before = addDays(day, -1)
  const after = addDays(day, 1)
  return (isHoliday(before, holidays) && isWeekend(after))
    || (isWeekend(before) && isHoliday(after, holidays))
}

type StaySpan = Pick<StayLike, 'kind' | 'starts_on' | 'ends_on'>

/** The leave marks a stay write carries — the rule both shells write by, so
 *  the phone's form and the desktop's sheet leave `leave_days` the same.
 *
 *  `before` null is a create, `after` null a delete. A new travel stay marks
 *  its weekdays that are not holidays of the region being looked at (`defaultLeaveFor`); a travel
 *  stay whose dates change carries its marks as the desktop's drag does
 *  (`leaveDiff`); a deleted stay takes every mark inside it, as the
 *  desktop's Delete does. A second home marks nothing — Doug works from there (Ireland) —
 *  so a second-home stay's own marks are the single days he set by hand, and
 *  moving one leaves them where they are.
 *
 *  A change of kind has no desktop gesture to copy (its kind select writes
 *  no marks; its pane has the buttons to fix them), and the phone has no
 *  ruler to fix them on, so: travel to the second home clears the trip's marks, and
 *  the second home to travel marks the new span as a new trip would.
 *
 *  `clear` and `set` never overlap, and neither repeats a day already in
 *  that state. Dates unchanged on a travel stay is no change: `leaveDiff`
 *  would clear a hand-marked weekend inside it as one that "cannot be leave". */
export function stayLeaveChange(
  before: StaySpan | null, after: StaySpan | null,
  marked: ReadonlySet<string>, holidays: Record<string, unknown>,
): { clear: string[]; set: string[] } {
  const none = { clear: [], set: [] }
  const inside = (s: StaySpan) => [...marked].filter(d => d >= s.starts_on && d <= s.ends_on).sort()
  const fresh = (s: StaySpan) => ({ clear: [], set: defaultLeaveFor(s, holidays).filter(d => !marked.has(d)) })
  if (!before) return after ? fresh(after) : none
  if (!after) return { clear: inside(before), set: [] }
  const wasTrip = before.kind === 'travel'
  const isTrip = after.kind === 'travel'
  if (!wasTrip && !isTrip) return none
  if (!wasTrip) return fresh(after)
  if (!isTrip) return { clear: inside(before), set: [] }
  if (before.starts_on === after.starts_on && before.ends_on === after.ends_on) return none
  return leaveDiff(before, after, marked, holidays)
}
